import classes from "../homePageNew.module.css";
import { ENTITY_LABELS } from "../../../utils/labelMapping";
import { llmLabel } from "../api/homePageApi";
import { StarIcon } from "./icons";

function fmt(value, decimals) {
  if (value == null) return "—";
  return Number(value).toFixed(decimals);
}

// Which 4 metrics the Index (RBI) column depends on — same set Category
// Analysis's own launch form/real dashboard treat as "all metrics selected"
// (CategoryScoreCardsNew.js's RBI_REQUIRED_METRICS). Kept local rather than
// imported since that file also pulls in the full real Category Analysis
// module.
const RBI_REQUIRED_METRICS = ["rank", "sentiment", "coreAttribute", "categoryDimension"];

/**
 * Generic per-LLM score table — one row per LLM that ran the selected
 * combination, so a module running several models at once (Brand Audit)
 * can show them side by side instead of making the user select one at a
 * time. Takes plain rows of { llm, scores }, so any module can reuse it.
 *
 * The primary LLM's row is marked AND sorted to the top, since its
 * combination is what the donut and Key Insights above the table are
 * showing — it shouldn't require scrolling past other models to find.
 * Marked with a star icon rather than a text "primary" tag: a text badge
 * next to a long model name has nowhere to go inside the truncating name
 * cell and gets squeezed out entirely — an icon has a fixed footprint and
 * always stays visible.
 *
 * `visibleMetrics` (Category Analysis ONLY — every other module leaves it
 * null) — that record's launch-time selectedMetrics values ("rank"/
 * "sentiment"/"coreAttribute"/"categoryDimension"). When given, only the
 * matching score columns render, and the Index/RBI column itself only
 * shows once all 4 are present — mirrors the real Category Analysis
 * dashboard's own column visibility (CategoryScoreCardsNew.js). Omitted
 * (null) everywhere else, which renders the original full table unchanged.
 */
export default function LlmScoreTable({ rows, primaryLLM, isProduct, visibleMetrics = null }) {
  if (!rows || rows.length === 0) return null;

  const pillarsLabel = isProduct ? ENTITY_LABELS.productDimension : ENTITY_LABELS.brandPillar;
  const isPrimaryRow = (row) => primaryLLM && String(row.llm).toLowerCase() === String(primaryLLM).toLowerCase();
  const sortedRows = primaryLLM
    ? [...rows].sort((a, b) => (isPrimaryRow(b) ? 1 : 0) - (isPrimaryRow(a) ? 1 : 0))
    : rows;

  const metricDefs = [
    { metricKey: "rank", label: ENTITY_LABELS.rank, cell: (row) => fmt(row.scores?.visibility_score, 0) },
    { metricKey: "sentiment", label: "Sentiment", cell: (row) => fmt(row.scores?.sentiment_score, 0) },
    { metricKey: "coreAttribute", label: pillarsLabel, cell: (row) => fmt(row.scores?.brand_pillars_score, 1) },
    { metricKey: "categoryDimension", label: ENTITY_LABELS.purchaseCriteria, cell: (row) => fmt(row.scores?.purchase_criteria_score, 1) },
  ];
  const visibleDefs = visibleMetrics ? metricDefs.filter((d) => visibleMetrics.includes(d.metricKey)) : metricDefs;
  const showIndex = visibleMetrics ? RBI_REQUIRED_METRICS.every((m) => visibleMetrics.includes(m)) : true;

  return (
    <div className={classes.llmTableWrap}>
      <table className={classes.llmTable}>
        <thead>
          <tr>
            <th>LLM</th>
            {showIndex && <th>Index</th>}
            {visibleDefs.map((d) => (
              <th key={d.metricKey}>{d.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => {
            const isPrimary = isPrimaryRow(row);
            return (
              <tr key={row.llm} className={isPrimary ? classes.llmRowPrimary : undefined}>
                <td className={classes.llmName} title={llmLabel(row.llm)}>
                  {isPrimary && (
                    <span className={classes.llmPrimaryIcon} title="Primary LLM">
                      <StarIcon size={12} />
                    </span>
                  )}
                  <span className={classes.llmNameText}>{llmLabel(row.llm)}</span>
                </td>
                {showIndex && <td className={classes.llmScoreStrong}>{fmt(row.scores?.brand_index_score, 0)}</td>}
                {visibleDefs.map((d) => (
                  <td key={d.metricKey}>{d.cell(row)}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
